import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Crown, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StripeCheckoutSheet } from "./StripeCheckoutSheet";
import { PlanSelector } from "./PlanSelector";

export function SubscriptionGate({ needsPlan, needsDues }) {
  const { user, family, refreshUser, logout } = useAuth();
  const [tier, setTier] = useState(family?.tier || user?.subscription_tier || "branches");
  const [billing, setBilling] = useState(user?.billing || "monthly");
  const [loading, setLoading] = useState(false);
  const [checkout, setCheckout] = useState(null); // {kind, client_secret, session_id}
  const [switching, setSwitching] = useState(false);
  const [dues, setDues] = useState(null);
  const [plans, setPlans] = useState(null);

  useEffect(() => { (async () => { try { const { data } = await api.get("/dues/info"); setDues(data); } catch {} })(); }, []);
  useEffect(() => { (async () => { try { const { data } = await api.get("/plans"); setPlans(data.plans); } catch {} })(); }, []);

  const planIsPaid = tier !== "roots";
  const stillNeedsPlan = needsPlan && planIsPaid;

  const planPrice = () => {
    if (!plans || !plans[tier]) return tier === "roots" ? "Free" : "";
    const p = plans[tier].price;
    if (tier === "roots" || (!p.monthly && !p.yearly)) return "Free";
    return billing === "monthly" ? `$${p.monthly.toFixed(2)}/mo` : `$${p.yearly.toFixed(2)}/yr`;
  };

  const payDues = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/dues/checkout", { tier, billing });
      if (data.status === "none") { await refreshUser(); return; }
      setCheckout({ kind: "dues", client_secret: data.client_secret, session_id: data.session_id });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const start = async () => {
    setLoading(true);
    try {
      // Switched to (or is) Roots -> free, activate immediately
      if (tier === "roots") {
        await api.post("/subscription/checkout", { tier: "roots", billing, origin_url: window.location.origin });
        if (needsDues && !user?.dues_active) { setLoading(false); await payDues(); return; }
        await refreshUser();
        return;
      }
      if (stillNeedsPlan) {
        const { data } = await api.post("/subscription/checkout", { tier, billing, origin_url: window.location.origin });
        if (data.status === "active") { await refreshUser(); if (needsDues) await payDues(); return; }
        setCheckout({ kind: "plan", client_secret: data.client_secret, session_id: data.session_id });
      } else if (needsDues) {
        setLoading(false);
        await payDues();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const onPaid = async () => {
    const kind = checkout?.kind;
    setCheckout(null);
    if (kind === "plan") {
      toast.success("Plan activated!");
      await refreshUser();
      if (needsDues) { await payDues(); }
    } else {
      toast.success("Dues set up!");
      await refreshUser();
    }
  };

  if (switching) {
    return (
      <div className="min-h-screen px-7 py-14 relative z-10" data-testid="gate-switch">
        <button onClick={() => setSwitching(false)} className="mb-5 flex items-center gap-1 text-sm gold-text"><ArrowLeft size={16} /> Back</button>
        <h1 className="font-serif-display text-3xl mb-1">Switch your plan</h1>
        <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>Changed your mind? Pick the plan you'd like before activating.</p>
        <PlanSelector initialTier={tier} ctaLabel="Choose this plan" onSelect={(t, b) => { setTier(t); setBilling(b); setSwitching(false); toast.success(`${t[0].toUpperCase() + t.slice(1)} selected`); }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-7 py-14 relative z-10 grid place-items-center text-center" data-testid="subscription-gate">
      <div className="w-full">
        <div className="h-14 w-14 rounded-2xl grid place-items-center mx-auto mb-4" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)" }}>
          <Crown size={26} className="gold-text" />
        </div>
        <h1 className="font-serif-display text-3xl">Welcome to the family!</h1>
        <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
          You've been approved for <b className="gold-text">{family?.name}</b>. Confirm what you owe to enter the vault.
        </p>

        <div className="card-surface rounded-2xl p-4 mt-6 text-left space-y-2" data-testid="gate-summary">
          {planIsPaid && (
            <div className="flex items-center justify-between text-sm">
              <span className="capitalize">{tier} plan ({billing})</span>
              <span className="gold-text font-semibold" data-testid="gate-plan-price">{planPrice()}</span>
            </div>
          )}
          {!planIsPaid && (
            <div className="flex items-center justify-between text-sm">
              <span className="capitalize">{tier} plan</span>
              <span className="gold-text font-semibold" data-testid="gate-plan-price">Free</span>
            </div>
          )}
          {needsDues && dues && dues.dues_amount > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span>Family dues (incl. 5% fee)</span>
              <span className="gold-text font-semibold" data-testid="gate-dues-total">${Number(dues.member_total).toFixed(2)} {dues.frequency}</span>
            </div>
          )}
        </div>

        {planIsPaid && (
          <div className="flex gap-2 p-1 rounded-2xl mt-4 max-w-xs mx-auto" style={{ background: "var(--overlay)" }}>
            {["monthly", "yearly"].map((b) => (
              <button key={b} onClick={() => setBilling(b)} data-testid={`gate-billing-${b}`}
                className="flex-1 rounded-xl py-2 text-xs font-semibold"
                style={billing === b ? { background: "var(--accent)", color: "#fff" } : { color: "var(--text-secondary)" }}>
                {b === "monthly" ? "Monthly" : "Yearly"}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-center gap-1.5 mt-5 text-[11px]" style={{ color: "var(--text-muted)" }}>
          <ShieldCheck size={13} className="gold-text" /> You are only charged now that you've been admitted.
        </div>

        <motion.button whileTap={{ scale: 0.97 }} onClick={start} disabled={loading}
          data-testid="gate-pay-btn"
          className="w-full mt-4 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
          style={{ background: "var(--accent)", color: "#fff" }}>
          {loading && <Loader2 size={16} className="animate-spin" />} Activate & enter the vault
        </motion.button>

        <button onClick={() => setSwitching(true)} className="mt-4 text-sm gold-text font-medium" data-testid="gate-switch-plans-btn">Switch Plans</button>
        <div><button onClick={logout} className="mt-3 text-xs" style={{ color: "var(--text-muted)" }} data-testid="gate-logout-btn">Log out</button></div>
      </div>

      <StripeCheckoutSheet open={!!checkout} clientSecret={checkout?.client_secret} sessionId={checkout?.session_id}
        onClose={() => setCheckout(null)} onPaid={onPaid} />
    </div>
  );
}
